//* Libraries imports
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  EditIcon,
  CalendarIcon,
  DollarSignIcon,
  UserIcon,
  UserCheckIcon,
  PackageIcon,
  MailIcon,
  PhoneIcon,
  CreditCardIcon,
  FileTextIcon,
  MapPinIcon,
  ClockIcon,
  HashIcon,
  ReceiptIcon,
  AlertCircleIcon,
} from "lucide-react";

//* Hooks Imports
import { useGetSale } from "@hooks/sales/queries/useGetSale";

//* Helpers Imports
import { getStatusLabel, getStatusColor } from "@helpers/paymentHelper";
import { getSaleCashPrice, getSaleInstallmentPrice } from "@helpers/saleHelper";

//* Utils Imports
import { formatDateTime } from "@/utils/formatDate";
import { formatCurrency } from "@/utils/currencyUtils";

//* Type Imports
import type { ISale } from "@/schemas";
import { MethodPaymentEnumLabel } from "@/schemas/methodpayment";

function InfoRow({
  icon: Icon,
  label,
  value,
  className = "",
}: {
  icon: any;
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
        <Icon className="size-3.5" />
        {label}
      </span>
      <div className="font-medium text-gray-700 text-sm break-words">
        {value || <span className="text-gray-400 italic">Não informado</span>}
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title }: { icon: any; title: string }) {
  return (
    <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2 pb-2 border-b border-gray-100">
      <Icon className="size-5 text-blue-500" />
      {title}
    </h2>
  );
}

export function SalesView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const queryGetSale = useGetSale(id);

  if (queryGetSale.isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  const sale = queryGetSale.data as ISale;

  if (!sale) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 min-h-[400px]">
        <p className="text-xl font-medium text-gray-600">
          Venda não encontrada
        </p>
        <button
          id="back-to-list"
          onClick={() => navigate("/sales")}
          className="flex items-center gap-2 text-blue-500 hover:text-blue-600 font-medium"
        >
          <ArrowLeftIcon size={20} />
          Voltar para lista
        </button>
      </div>
    );
  }

  const cashPrice = getSaleCashPrice(sale);
  const installmentPrice = getSaleInstallmentPrice(sale);
  const soldPrice = sale.paymentDetails?.value;
  const isCustomPrice =
    sale.financialConfig?.cashPrice !== undefined ||
    sale.financialConfig?.installmentPrice !== undefined;

  const client = typeof sale.clientId === "object" ? sale.clientId : null;
  const product = typeof sale.productId === "object" ? sale.productId : null;
  const responsible =
    typeof sale.responsibleId === "object" ? sale.responsibleId : null;
  const mainAddress = client?.places?.[0];

  return (
    <div className="w-full max-w-7xl mx-auto p-6 space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <button
            onClick={() => navigate("/sales")}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 font-medium transition-colors mb-2"
          >
            <ArrowLeftIcon size={18} />
            Voltar para lista
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">
              Venda #{sale.orderNumber || sale.id?.slice(0, 8)}
            </h1>
            <div
              className="px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1.5"
              style={{
                backgroundColor: `${getStatusColor(sale.paymentStatus as any)}15`,
                color: getStatusColor(sale.paymentStatus as any),
                borderColor: `${getStatusColor(sale.paymentStatus as any)}30`,
              }}
            >
              <div
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  backgroundColor: getStatusColor(sale.paymentStatus as any),
                }}
              />
              {getStatusLabel(sale.paymentStatus as any)}
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate(`/sales/edit/${id}`)}
          className="flex items-center justify-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors font-medium shadow-sm hover:shadow-md active:scale-95 duration-200"
        >
          <EditIcon size={18} />
          Editar Venda
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <SectionHeader icon={UserIcon} title="Dados do Cliente" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoRow
                icon={UserIcon}
                label="Nome Completo"
                value={client?.name}
              />
              <InfoRow
                icon={FileTextIcon}
                label="CPF/CNPJ"
                value={client?.cpf || client?.cnpj}
              />
              <InfoRow icon={MailIcon} label="E-mail" value={client?.email} />
              <InfoRow
                icon={PhoneIcon}
                label="WhatsApp / Telefone"
                value={client?.whatsapp || (client as any)?.phone}
              />
              {mainAddress && (
                <div className="md:col-span-2 pt-4 border-t border-gray-50 mt-2">
                  <InfoRow
                    icon={MapPinIcon}
                    label="Endereço Principal"
                    value={`${mainAddress.street || ""}, ${
                      mainAddress.number || "S/N"
                    } ${mainAddress.complement ? `(${mainAddress.complement})` : ""} - ${
                      mainAddress.neighborhood || ""
                    }, ${mainAddress.cityName || ""}/${
                      mainAddress.stateCode || ""
                    } - CEP: ${mainAddress.zip_code || ""}`}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <SectionHeader icon={PackageIcon} title="Produto e Detalhes" />

            {product?.image && (
              <div className="mb-6 flex items-center gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <img
                  id="product-image"
                  src={product.image}
                  alt={product.name}
                  className="w-16 h-16 rounded-lg object-cover bg-white shadow-sm"
                />
                <div>
                  <p className="font-bold text-gray-800">{product.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {product.description || "Sem descrição"}
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {!product?.image && (
                <InfoRow
                  icon={PackageIcon}
                  label="Produto"
                  value={product?.name}
                />
              )}
              <InfoRow
                icon={HashIcon}
                label="Tipo de Venda"
                value={sale.saleType}
              />
              <InfoRow
                icon={CalendarIcon}
                label="Data da Venda"
                value={formatDateTime(
                  sale.saleDate,
                  "dd 'de' MMM, yyyy 'às' HH:mm",
                )}
              />
              <InfoRow
                icon={UserCheckIcon}
                label="Responsável"
                value={responsible?.name}
              />
              <div className="md:col-span-2 grid grid-cols-2 gap-6 pt-4 border-t border-gray-50 mt-2">
                <InfoRow
                  icon={ClockIcon}
                  label="Criado em"
                  value={formatDateTime(
                    sale.createdAt,
                    "dd 'de' MMM, yyyy 'às' HH:mm",
                  )}
                />
                <InfoRow
                  icon={ClockIcon}
                  label="Atualizado em"
                  value={formatDateTime(
                    sale.updatedAt,
                    "dd 'de' MMM, yyyy 'às' HH:mm",
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <SectionHeader icon={DollarSignIcon} title="Financeiro" />

            {isCustomPrice && (
              <div className="mb-6 p-3 bg-amber-50 border border-amber-100 rounded-lg flex gap-2">
                <AlertCircleIcon className="size-5 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 font-medium">
                  Esta venda possui configuração de preço personalizada.
                </p>
              </div>
            )}

            <div className="space-y-6">
              {soldPrice !== undefined && soldPrice !== null && (
                <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <p className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1">
                    Valor Vendido
                  </p>
                  <p className="text-2xl font-bold text-purple-700">
                    {formatCurrency(soldPrice)}
                  </p>
                </div>
              )}

              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">
                  Valor à Vista
                </p>
                <p className="text-2xl font-bold text-emerald-700">
                  {formatCurrency(cashPrice)}
                </p>
              </div>

              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">
                  Valor a Prazo
                </p>
                <p className="text-2xl font-bold text-blue-700">
                  {formatCurrency(installmentPrice)}
                </p>
              </div>

              {sale.financialConfig && (
                <div className="pt-4 border-t border-gray-50 space-y-3">
                  {sale.financialConfig.maxInstallments && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Max. Parcelas</span>
                      <span className="font-medium text-gray-900">
                        {sale.financialConfig.maxInstallments}x
                      </span>
                    </div>
                  )}
                  {sale.financialConfig.interestRate && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-500">Taxa de Juros</span>
                      <span className="font-medium text-gray-900">
                        {sale.financialConfig.interestRate}% a.m
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <SectionHeader icon={ReceiptIcon} title="Pagamento" />
            <div className="space-y-4">
              <InfoRow
                icon={CreditCardIcon}
                label="Método"
                value={
                  sale.paymentDetails?.method
                    ? MethodPaymentEnumLabel[
                        sale.paymentDetails
                          .method as keyof typeof MethodPaymentEnumLabel
                      ] || sale.paymentDetails.method
                    : null
                }
              />

              {sale.paymentDetails?.method === "CREDIT_CARD" && (
                <div className="pt-3 border-t border-gray-50 grid grid-cols-2 gap-y-4 gap-x-2">
                  {(sale.paymentDetails as any).installments && (
                    <InfoRow
                      icon={CalendarIcon}
                      label="Parcelas"
                      value={`${(sale.paymentDetails as any).installments}x`}
                    />
                  )}
                  {((sale.paymentDetails as any).cardBrand ||
                    (sale.paymentDetails as any).creditCard?.brand) && (
                    <InfoRow
                      icon={CreditCardIcon}
                      label="Bandeira"
                      value={
                        (sale.paymentDetails as any).cardBrand ||
                        (sale.paymentDetails as any).creditCard?.brand
                      }
                    />
                  )}
                </div>
              )}

              {sale.paymentStatus === "TED" && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 border border-gray-100">
                  Comprovante de TED enviado.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
